// Chart-specific data transformation helpers

export const getLineChartData = (processedTrendData, showBreakdown, colors, average) => {
  return {
    labels: processedTrendData.map(item => item.displayDate),
    datasets: showBreakdown ? [
      {
        label: 'Total',
        data: processedTrendData.map(item => item.count),
        borderColor: colors.total,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'No Diets',
        data: processedTrendData.map(item => item.noDiets),
        borderColor: colors.noDiets,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'Diets With Warning',
        data: processedTrendData.map(item => item.dietsWithWarning),
        borderColor: colors.dietsWithWarning,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'Successful Diets',
        data: processedTrendData.map(item => item.successfulDiets),
        borderColor: colors.successfulDiets,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ] : [
      {
        label: 'Total',
        data: processedTrendData.map(item => item.count),
        borderColor: colors.total,
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        fill: false,
        pointBackgroundColor: colors.total,
        pointBorderColor: colors.isDark ? '#1f2937' : '#ffffff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
      {
        label: 'Average',
        data: Array(processedTrendData.length).fill(average),
        borderColor: colors.average,
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        fill: false,
      },
    ],
  };
};

export const getBarChartData = (processedTrendData, showBreakdown, colors) => {
  return {
    labels: processedTrendData.map(item => item.displayDate),
    datasets: showBreakdown ? [
      {
        label: 'Successful Diets',
        data: processedTrendData.map(item => item.successfulDiets),
        backgroundColor: colors.successfulDiets,
        borderColor: colors.successfulDiets,
        borderWidth: 1,
      },
      {
        label: 'Diets With Warning',
        data: processedTrendData.map(item => item.dietsWithWarning),
        backgroundColor: colors.dietsWithWarning,
        borderColor: colors.dietsWithWarning,
        borderWidth: 1,
      },
      {
        label: 'No Diets',
        data: processedTrendData.map(item => item.noDiets),
        backgroundColor: colors.noDiets,
        borderColor: colors.noDiets,
        borderWidth: 1,
      },
    ] : [
      {
        label: 'Total',
        data: processedTrendData.map(item => item.count),
        backgroundColor: colors.total,
        borderColor: colors.total,
        borderWidth: 1,
      },
    ],
  };
};

export const getAreaChartData = (processedTrendData, showBreakdown, colors, average, isStacked = true) => {
  const baseData = getLineChartData(processedTrendData, showBreakdown, colors, average);
  
  if (showBreakdown) {
    // For area chart in breakdown mode, modify the fill behavior based on stacked mode
    // In stacked mode, exclude the total line as it's redundant
    const datasetsToUse = isStacked 
      ? baseData.datasets.filter(dataset => dataset.label !== 'Total')
      : baseData.datasets;
    
    baseData.datasets = datasetsToUse.map((dataset, index) => {
      let backgroundColor;
      // Set proper background colors based on dataset label
      if (dataset.label === 'No Diets') {
        backgroundColor = colors.noDietsBg;
      } else if (dataset.label === 'Diets With Warning') {
        backgroundColor = colors.dietsWithWarningBg;
      } else if (dataset.label === 'Successful Diets') {
        backgroundColor = colors.successfulDietsBg;
      } else {
        backgroundColor = 'transparent'; // Total line should remain transparent
      }
      
      return {
        ...dataset,
        fill: isStacked ? (index === 0 ? 'origin' : '-1') : 'origin', // Stacked: stack on previous, Non-stacked: fill to origin
        backgroundColor,
        borderWidth: 1,
      };
    });
  } else {
    // For summary mode, keep single filled area
    baseData.datasets = baseData.datasets.map((dataset) => ({
      ...dataset,
      fill: true,
      backgroundColor: colors.isDark ? '#60a5fa35' : '#2563eb25',
    }));
  }
  
  return baseData;
};

export const getDoughnutChartData = (processedTrendData, colors) => {
  // Calculate totals for the entire period
  const totalNoDiets = processedTrendData.reduce((sum, item) => sum + item.noDiets, 0);
  const totalDietsWithWarning = processedTrendData.reduce((sum, item) => sum + item.dietsWithWarning, 0);
  const totalSuccessfulDiets = processedTrendData.reduce((sum, item) => sum + item.successfulDiets, 0);
  
  return {
    labels: ['Successful Diets', 'Diets With Warning', 'No Diets'],
    datasets: [
      {
        data: [totalSuccessfulDiets, totalDietsWithWarning, totalNoDiets],
        backgroundColor: [
          colors.successfulDiets,
          colors.dietsWithWarning,
          colors.noDiets,
        ],
        borderColor: [
          colors.successfulDiets,
          colors.dietsWithWarning,
          colors.noDiets,
        ],
        borderWidth: 1,
      },
    ],
  };
};

const getTooltipLabelColor = (context, colors) => {
  const datasetLabel = context.dataset.label;
  let color;
  
  switch (datasetLabel) {
    case 'No Diets':
      color = colors.noDiets;
      break;
    case 'Diets With Warning':
      color = colors.dietsWithWarning;
      break;
    case 'Successful Diets':
      color = colors.successfulDiets;
      break;
    case 'Total':
      color = colors.total;
      break;
    case 'Average':
      color = colors.average;
      break;
    default:
      color = context.dataset.borderColor || context.dataset.backgroundColor;
      break;
  }
  
  return {
    backgroundColor: color,
    borderColor: color,
  };
};

export const getChartOptions = (chartType, isDark, selectedPeriod, areaChartStacked = true, colors = null) => {
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 750, // Normal animations for data updates, chart changes, etc.
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
  };

  switch (chartType) {
    case 'line':
      return {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          tooltip: {
            backgroundColor: isDark ? '#374151' : '#ffffff',
            titleColor: isDark ? '#f9fafb' : '#111827',
            bodyColor: isDark ? '#d1d5db' : '#374151',
            borderColor: isDark ? '#4b5563' : '#d1d5db',
            borderWidth: 1,
            mode: 'index',
            intersect: false,
            displayColors: true,
            usePointStyle: false,
            boxWidth: 12,
            boxHeight: 12,
            boxPadding: 4,
            callbacks: {
              labelColor: function(context) {
                return colors ? getTooltipLabelColor(context, colors) : {
                  backgroundColor: context.dataset.borderColor,
                  borderColor: context.dataset.borderColor,
                };
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              color: isDark ? '#d1d5db' : '#374151',
              maxTicksLimit: selectedPeriod === '1Y' ? 12 : selectedPeriod === '6M' ? 18 : 10,
            },
            grid: {
              color: isDark ? '#374151' : '#f3f4f6',
            },
          },
          y: {
            ticks: {
              color: isDark ? '#d1d5db' : '#374151',
            },
            grid: {
              color: isDark ? '#374151' : '#f3f4f6',
            },
          },
        },
      };

    case 'area':
      return {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          tooltip: {
            backgroundColor: isDark ? '#374151' : '#ffffff',
            titleColor: isDark ? '#f9fafb' : '#111827',
            bodyColor: isDark ? '#d1d5db' : '#374151',
            borderColor: isDark ? '#4b5563' : '#d1d5db',
            borderWidth: 1,
            mode: 'index',
            intersect: false,
            displayColors: true,
            usePointStyle: false,
            boxWidth: 12,
            boxHeight: 12,
            boxPadding: 4,
            callbacks: {
              labelColor: function(context) {
                return colors ? getTooltipLabelColor(context, colors) : {
                  backgroundColor: context.dataset.borderColor,
                  borderColor: context.dataset.borderColor,
                };
              },
            },
          },
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
        scales: {
          x: {
            stacked: areaChartStacked,
            ticks: {
              color: isDark ? '#d1d5db' : '#374151',
              maxTicksLimit: selectedPeriod === '1Y' ? 12 : selectedPeriod === '6M' ? 18 : 10,
            },
            grid: {
              color: isDark ? '#374151' : '#f3f4f6',
            },
          },
          y: {
            stacked: areaChartStacked,
            ticks: {
              color: isDark ? '#d1d5db' : '#374151',
            },
            grid: {
              color: isDark ? '#374151' : '#f3f4f6',
            },
          },
        },
      };

    case 'bar':
      return {
        ...baseOptions,
        plugins: {
          ...baseOptions.plugins,
          tooltip: {
            backgroundColor: isDark ? '#374151' : '#ffffff',
            titleColor: isDark ? '#f9fafb' : '#111827',
            bodyColor: isDark ? '#d1d5db' : '#374151',
            borderColor: isDark ? '#4b5563' : '#d1d5db',
            borderWidth: 1,
            mode: 'index',
            intersect: false,
            displayColors: true,
            usePointStyle: false,
            boxWidth: 12,
            boxHeight: 12,
            boxPadding: 4,
            callbacks: {
              labelColor: function(context) {
                return colors ? getTooltipLabelColor(context, colors) : {
                  backgroundColor: context.dataset.backgroundColor,
                  borderColor: context.dataset.borderColor,
                };
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              color: isDark ? '#d1d5db' : '#374151',
              maxTicksLimit: selectedPeriod === '1Y' ? 12 : selectedPeriod === '6M' ? 18 : 10,
            },
            grid: {
              color: isDark ? '#374151' : '#f3f4f6',
            },
            categoryPercentage: 0.8,
            barPercentage: 0.9,
          },
          y: {
            ticks: {
              color: isDark ? '#d1d5db' : '#374151',
            },
            grid: {
              color: isDark ? '#374151' : '#f3f4f6',
            },
          },
        },
      };

    case 'doughnut':
      return {
        ...baseOptions,
        cutout: '60%',
        plugins: {
          ...baseOptions.plugins,
          legend: {
            display: false,
            position: 'right',
            labels: {
              color: isDark ? '#d1d5db' : '#374151',
              usePointStyle: true,
              padding: 15,
            },
          },
          tooltip: {
            backgroundColor: isDark ? '#374151' : '#ffffff',
            titleColor: isDark ? '#f9fafb' : '#111827',
            bodyColor: isDark ? '#d1d5db' : '#374151',
            borderColor: isDark ? '#4b5563' : '#d1d5db',
            borderWidth: 1,
            displayColors: true,
            usePointStyle: false,
            boxWidth: 12,
            boxHeight: 12,
            boxPadding: 4,
            callbacks: {
              label: function(context) {
                const total = context.dataset.data.reduce((sum, value) => sum + value, 0);
                const percentage = Math.round((context.parsed / total) * 100);
                return `${context.label}: ${context.parsed} (${percentage}%)`;
              },
              labelColor: function(context) {
                return colors ? getTooltipLabelColor(context, colors) : {
                  backgroundColor: context.dataset.backgroundColor[context.dataIndex],
                  borderColor: context.dataset.borderColor[context.dataIndex],
                };
              },
            },
          },
        },
      };

    default:
      return baseOptions;
  }
};